import { clamp } from '../core/math';
import { KeyboardSmoother, quantiseBrake, quantiseSteer } from './controls';

const STICK_DEADZONE = 0.12;
const TRIGGER_DEADZONE = 0.04;

/**
 * Reads keyboard, touch and gamepad, and reduces all three to a quantised
 * steer/brake pair per physics tick. That pair is the replay: re-run it through
 * the same sim and the lap reproduces, which is what lets a server verify a
 * submitted time later.
 */
export class InputManager {
  private heldLeft = new Set<string>();
  private heldRight = new Set<string>();
  private heldBrake = new Set<string>();

  private get keyLeft(): boolean {
    return this.heldLeft.size > 0;
  }
  private get keyRight(): boolean {
    return this.heldRight.size > 0;
  }
  private get keyBrake(): boolean {
    return this.heldBrake.size > 0;
  }

  private touchLeft = false;
  private touchRight = false;
  private touchBrake = false;

  private readonly smoother = new KeyboardSmoother();

  /** True while a gamepad is supplying input, for the "analogue" HUD hint. */
  gamepadActive = false;

  private onRestart: (() => void) | null = null;
  private onStart: (() => void) | null = null;

  attach(root: HTMLElement): void {
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    window.addEventListener('blur', this.releaseAll);
    this.bindTouchZones(root);
  }

  detach(): void {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('blur', this.releaseAll);
  }

  setCallbacks(onStart: () => void, onRestart: () => void): void {
    this.onStart = onStart;
    this.onRestart = onRestart;
  }

  reset(): void {
    this.smoother.reset();
  }

  private isGameKey(code: string): boolean {
    return (
      code === 'ArrowLeft' ||
      code === 'ArrowRight' ||
      code === 'ArrowDown' ||
      code === 'ArrowUp' ||
      code === 'Space'
    );
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (this.isGameKey(e.code)) e.preventDefault();
    if (e.repeat) return;
    switch (e.code) {
      case 'ArrowLeft':
      case 'KeyA':
        this.heldLeft.add(e.code);
        break;
      case 'ArrowRight':
      case 'KeyD':
        this.heldRight.add(e.code);
        break;
      case 'Space':
      case 'ArrowDown':
      case 'KeyS':
        this.heldBrake.add(e.code);
        this.onStart?.();
        break;
      case 'Enter':
        this.onStart?.();
        break;
      case 'KeyR':
        this.onRestart?.();
        break;
      default:
        break;
    }
  };

  private handleKeyUp = (e: KeyboardEvent): void => {
    switch (e.code) {
      case 'ArrowLeft':
      case 'KeyA':
        this.heldLeft.delete(e.code);
        break;
      case 'ArrowRight':
      case 'KeyD':
        this.heldRight.delete(e.code);
        break;
      case 'Space':
      case 'ArrowDown':
      case 'KeyS':
        this.heldBrake.delete(e.code);
        break;
      default:
        break;
    }
  };

  private releaseAll = (): void => {
    this.heldLeft.clear();
    this.heldRight.clear();
    this.heldBrake.clear();
    this.touchLeft = this.touchRight = this.touchBrake = false;
  };

  private bindTouchZones(root: HTMLElement): void {
    const zones: Array<[string, (on: boolean) => void]> = [
      ['touch-left', (on) => (this.touchLeft = on)],
      ['touch-right', (on) => (this.touchRight = on)],
      ['touch-brake', (on) => (this.touchBrake = on)],
    ];
    for (const [id, set] of zones) {
      const el = root.querySelector<HTMLElement>(`#${id}`);
      if (!el) continue;
      el.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        el.setPointerCapture(e.pointerId);
        set(true);
        this.onStart?.();
      });
      const release = (e: PointerEvent): void => {
        e.preventDefault();
        set(false);
      };
      el.addEventListener('pointerup', release);
      el.addEventListener('pointercancel', release);
    }
  }

  /**
   * A connected pad bypasses the keyboard ramp entirely — it already gives
   * analogue values, and smoothing them a second time would add lag and blunt
   * exactly the fine brake modulation a pad is worth having for.
   */
  private readGamepad(): { steer: number; brake: number } | null {
    const pads = navigator.getGamepads?.() ?? [];
    for (const pad of pads) {
      if (!pad) continue;

      const rawSteer = pad.axes[0] ?? 0;
      // Left trigger, then right trigger, then A — whichever this pad exposes.
      const rawBrake = Math.max(
        pad.buttons[6]?.value ?? 0,
        pad.buttons[7]?.value ?? 0,
        pad.buttons[0]?.pressed ? 1 : 0,
      );

      const steer = Math.abs(rawSteer) < STICK_DEADZONE ? 0 : rawSteer;
      const brake = rawBrake < TRIGGER_DEADZONE ? 0 : rawBrake;

      if (steer !== 0 || brake !== 0 || pad.buttons.some((b) => b.pressed)) {
        if (pad.buttons[9]?.pressed || pad.buttons[0]?.pressed) this.onStart?.();
        if (pad.buttons[1]?.pressed) this.onRestart?.();
        this.gamepadActive = true;
        // Rescale past the deadzone so the usable travel still spans 0..1.
        const scaled =
          steer === 0 ? 0 : Math.sign(steer) * ((Math.abs(steer) - STICK_DEADZONE) / (1 - STICK_DEADZONE));
        return { steer: clamp(scaled, -1, 1), brake: clamp(brake, 0, 1) };
      }
    }
    return null;
  }

  /** Steer byte and brake byte for this tick. */
  sample(): { steerByte: number; brakeByte: number } {
    const pad = this.readGamepad();
    if (pad) {
      return { steerByte: quantiseSteer(pad.steer), brakeByte: quantiseBrake(pad.brake) };
    }
    this.gamepadActive = false;

    const { steer, brake } = this.smoother.step(
      this.keyLeft || this.touchLeft,
      this.keyRight || this.touchRight,
      this.keyBrake || this.touchBrake,
    );
    return { steerByte: quantiseSteer(steer), brakeByte: quantiseBrake(brake) };
  }
}
